import { uuidv7 } from '@kripod/uuidv7';
import z from 'zod';
const prefixes = {
    equipment: 'eqp',
    exercise: 'exr',
    exerciseComplex: 'exc',
    exerciseComplexItem: 'exi',
    locale: 'lcl',
    metric: 'mtr',
    movement: 'mvt',
    muscle: 'mus',
    sectionLog: 'scl',
    sectionPlan: 'scp',
    stepLog: 'stl',
    stepPlan: 'stp',
    setLog: 'slg',
    setPlan: 'spn',
    tag: 'tag',
    user: 'usr',
    workoutLog: 'wkl',
    workoutPlan: 'wkp',
    userWorkoutPlanSubscription: 'uwp',
};
/**
 * Generates a UUIDv7 identifier.
 * The prefix parameter is kept for type safety and documentation purposes,
 * but UUIDv7 IDs don't include prefixes in the final ID string.
 *
 * @param prefix - The entity type prefix (for type safety, not used in ID generation)
 * @returns A UUIDv7 string (e.g., "00ccebbc-13e0-7000-8b18-6150ad2d0c05")
 *
 * @example
 * const exerciseId = createID('exercise'); // Returns UUIDv7 string
 */
function createID(entityName) {
    return `${prefixes[entityName]}_${uuidv7()}`;
}
/**
 * A Zod schema builder for creating validators for prefixed UUIDv7 IDs.
 * This is the single source of truth for validating all database IDs in the application.
 *
 * It ensures that an ID string:
 * 1. Starts with the correct 3-letter prefix followed by an underscore.
 * 2. The part after the underscore is a valid UUIDv7.
 *
 * @param prefix - The entity name (e.g., 'user', 'workoutLog').
 * @returns A Zod schema that can be used to validate IDs.
 *
 * @example
 * const UserIdSchema = createIdSchema('user');
 * UserIdSchema.parse('usr_00ccebbc-13e0-7000-8b18-6150ad2d0c05'); // -> OK
 * UserIdSchema.parse('wkl_00ccebbc-13e0-7000-8b18-6150ad2d0c05'); // -> Throws ZodError
 * UserIdSchema.parse('usr_invalid-uuidv7');           // -> Throws ZodError
 */
function createIdSchema(entityName) {
    return z.string().refine((id) => {
        // Check if the prefix is valid
        if (prefixes[entityName] === undefined) {
            return false;
        }
        // Check if the ID starts with the expected prefix
        const expectedPrefix = `${prefixes[entityName]}_`;
        if (!id.startsWith(expectedPrefix)) {
            return false;
        }
        // Check if the UUIDv7 part is valid
        const uuidv7Part = id.slice(expectedPrefix.length);
        return z.uuidv7().safeParse(uuidv7Part).success;
    }, {
        message: `Invalid ID format. Expected '<prefix>_<uuidv7>'`,
    });
}
export { createID, createIdSchema };
//# sourceMappingURL=id.js.map